import { Injectable, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SplashOverlayService {
  /** Whether the splash overlay is currently active */
  readonly active = signal(false);

  /** Whether the splash is fading out */
  readonly fadingOut = signal(false);

  /** Timestamp when splash was shown */
  private showTime = 0;

  /** Minimum time (ms) to show the splash for logo animation to complete */
  private readonly MIN_DISPLAY_TIME = 2500;

  /**
   * Watchdog: no screen may keep the splash up forever. If nothing calls
   * dismiss() within this time (e.g. a target route that doesn't know
   * about the splash, or a hanging request), it dismisses itself.
   */
  private readonly MAX_DISPLAY_TIME = 15000;
  private watchdogTimer: ReturnType<typeof setTimeout> | null = null;

  /** Show the splash overlay */
  show(): void {
    this.fadingOut.set(false);
    this.active.set(true);
    this.showTime = Date.now();

    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
    }
    this.watchdogTimer = setTimeout(
      () => this.dismiss(),
      this.MAX_DISPLAY_TIME
    );
  }

  /**
   * Dismiss the splash with fade-out.
   * Ensures minimum display time for the logo animation to complete.
   */
  dismiss(): void {
    if (!this.active()) return;

    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }

    const elapsed = Date.now() - this.showTime;
    const remaining = Math.max(0, this.MIN_DISPLAY_TIME - elapsed);

    setTimeout(() => {
      this.fadingOut.set(true);
      setTimeout(() => {
        this.active.set(false);
        this.fadingOut.set(false);
      }, 800);
    }, remaining);
  }
}
